import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePromotionBanners } from "../../admin/contexts/PromotionBannerContext";
import { useTopSellingProducts } from "../contexts/TopSellingProductsContext";
import { usePreviousBuyingProducts } from "../contexts/PreviousBuyingContext";
import { supportTicketService } from "../../services/supportTicketService";
import type { SupportTicket } from "../../types/supportTicket";
import { useMessageModal } from "../contexts/MessageModalContext";
import { useCart } from "../../context/CartContext";
import { exportListingPdf } from "../../utils/exportListingPdf";
import TicketTable from "../../components/support/TicketTable";
import axios from "axios";
import "../assets/css/subscriberProfile.css";
import Avatar from "../components/Avatar";

type OrderTab =
  | "all"
  | "to_pay"
  | "to_ship"
  | "to_receive"
  | "completed"
  | "cancelled"
  | "return_refund";

interface OrderItem {
  id: number;
  orderItemId: number;
  barCode?: string;
  productName: string;
  image_path?: string;
  variant?: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
}

interface Order {
  id: number;
  orderId: string;
  buyerName: string;
  buyerEmail: string;
  buyerPhone: string;
  totalAmount: number;
  currency: string;
  paymentMethod: string;
  paymentStatus: string;
  orderStatus: string;
  createdAt: string;
  updatedAt: string;
  items: OrderItem[];
  status?: OrderTab;
}

interface ReviewFormState {
  rating: number;
  comment: string;
  photos: File[];
  videos: File[];
}

const SubscriberProfile: React.FC = () => {
  const navigate = useNavigate();
  const { addMultipleToCart } = useCart();
  const BASE = import.meta.env.VITE_LYRAZONE_BASE_URL;

  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("profile");
  const [data, setData] = useState<any>(null);

  const { banners, loading, getBannerImage } = usePromotionBanners();
  const validBanners = useMemo(() => banners || [], [banners]);

  const {
    products: topSellingProducts,
    loading: topSellingLoading,
    getProductImage,
  } = useTopSellingProducts();

  const validTopSellingProducts = useMemo(
    () => topSellingProducts || [],
    [topSellingProducts],
  );

  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const [topSellingIndex, setTopSellingIndex] = useState(0);
  const [isTopSellingHovered, setIsTopSellingHovered] = useState(false);

  const {
    products: previousBuyingProducts,
    loading: previousBuyingLoading,
    getProductImage: getPreviousBuyingImage,
  } = usePreviousBuyingProducts();

  const validPreviousBuyingProducts = useMemo(
    () => previousBuyingProducts || [],
    [previousBuyingProducts],
  );

  const [previousBuyingIndex, setPreviousBuyingIndex] = useState(0);
  const [isPreviousBuyingHovered, setIsPreviousBuyingHovered] = useState(false);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [orderActiveTab, setOrderActiveTab] = useState<OrderTab>("all");
  const [orders, setOrders] = useState<Order[]>([]);
  const [cancelLoadingOrderId, setCancelLoadingOrderId] = useState<
    string | null
  >(null);
  const { showModal } = useMessageModal();
  const [orderLoading, setOrderLoading] = useState(true);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company_name: "",
    country: "",
    city: "",
    phone: "",
    address: "",
    postal_code: "",
    recipient: "",
    delivery_country: "",
    delivery_city: "",
    delivery_phone: "",
    delivery_address: "",
    delivery_postal_code: "",
  });

  const currentUser = JSON.parse(
    localStorage.getItem("subscriber_user") || "{}",
  );
  const buyerEmail = currentUser?.email || localStorage.getItem("email") || "";
  const removeToken = async () => {
    try {
      await axios.post(`${BASE}/logout`, {}, { withCredentials: true });

      localStorage.removeItem("subscriber_user");
      sessionStorage.removeItem("userdata");

      navigate("/signin");
    } catch (error) {
      console.error("Logout failed");
    }
  };

  const fetchData = async () => {
    try {
      const response = await axios.get(`${BASE}/userdata`, {
        withCredentials: true,
      });

      sessionStorage.setItem(
        "userdata",
        JSON.stringify(response.data.userData[0]),
      );
      setData(response.data.userData[0]);
      setFormData(response.data.userData[0]);
    } catch (err) {
      console.error(err);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const response = await axios.put(`${BASE}/updateuser`, formData, {
        withCredentials: true,
      });

      showModal({
        type: "success",
        title: "Success",
        message: response.data?.message || "Profile updated successfully.",
      });
      setIsEditing(false);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchTickets = async () => {
    try {
      const res = await supportTicketService.getSubscriberTickets({
        page: 1,
        limit: 10,
      });
      setTickets(res.tickets || []);
    } catch (error) {
      console.error("Failed to fetch subscriber tickets", error);
    }
  };

  const resolveOrderStatus = (order: any): OrderTab => {
    const backendStatus = String(order.orderStatus || "").toLowerCase();

    switch (backendStatus) {
      case "cancelled":
        return "cancelled";
      case "completed":
        return "completed";
      case "return_refund":
        return "return_refund";
      case "to_receive":
      case "shipped":
        return "to_receive";
      case "to_ship":
      case "paid":
      case "processing":
        return "to_ship";
      case "to_pay":
        return "to_pay";
      case "pending":
      default:
        return order.paymentMethod !== "Cash on Delivery"
          ? "to_ship"
          : "to_pay";
    }
  };

  const fetchOrders = async () => {
    try {
      if (!buyerEmail) {
        setOrderLoading(false);
        return;
      }

      const response = await axios.get(
        `${
          import.meta.env.VITE_LYRAZONE_BASE_URL || "http://localhost:5000/api"
        }/orders`,
        {
          params: { buyerEmail },
        },
      );

      const mappedOrders = (response.data?.data || []).map((order: any) => ({
        ...order,
        status: resolveOrderStatus(order),
      }));

      setOrders(mappedOrders);
    } catch (error) {
      console.error("fetchOrders error:", error);
      setOrders([]);
    } finally {
      setOrderLoading(false);
    }
  };

  const tabs: { key: OrderTab; label: string }[] = [
    { key: "all", label: "All" },
    { key: "to_pay", label: "To Pay" },
    { key: "to_ship", label: "To Ship" },
    { key: "to_receive", label: "To Receive" },
    { key: "completed", label: "Completed" },
    { key: "cancelled", label: "Cancelled" },
    { key: "return_refund", label: "Return Refund" },
  ];

  const getTabCount = (tabKey: OrderTab) => {
    if (tabKey === "all") return orders.length;
    return orders.filter((order: any) => order.status === tabKey).length;
  };

  const filteredOrders = useMemo(() => {
    if (orderActiveTab === "all") return orders;
    return orders.filter((order: any) => order.status === orderActiveTab);
  }, [orderActiveTab, orders]);

  const [expandedReviewOrderId, setExpandedReviewOrderId] = useState<
    string | null
  >(null);
  const [reviewForms, setReviewForms] = useState<
    Record<string, ReviewFormState>
  >({});
  const [reviewSubmittingOrderId, setReviewSubmittingOrderId] = useState<
    number | null
  >(null);

  const getDefaultReviewForm = (): ReviewFormState => ({
    rating: 5,
    comment: "",
    photos: [],
    videos: [],
  });

  const getOrderProductImage = (item: OrderItem) => {
    if (item.image_path) {
      return `${import.meta.env.VITE_LYRAZONE_IMAGE_URL}/uploads/products/${item.id}/${item.image_path}`;
    }
    return "/placeholder-product.png";
  };

  const formatCurrency = (value: number) =>
    `RM${value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const getStatusLabel = (status: OrderTab) => {
    switch (status) {
      case "to_pay":
        return "TO PAY";
      case "to_ship":
        return "TO SHIP";
      case "to_receive":
        return "TO RECEIVE";
      case "completed":
        return "COMPLETED";
      case "cancelled":
        return "CANCELLED";
      case "return_refund":
        return "RETURN / REFUND";
      default:
        return "ALL";
    }
  };

  const canCancelOrder = (order: any) => {
    const status = String(order.status || "").toLowerCase();
    return status === "to_pay" || status === "to_ship";
  };

  const handleCancelOrder = async (orderId: string) => {
    const confirmed = window.confirm(
      "Are you sure you want to cancel this order?",
    );
    if (!confirmed) return;

    try {
      setCancelLoadingOrderId(orderId);

      const response = await axios.patch(
        `${
          import.meta.env.VITE_LYRAZONE_BASE_URL || "http://localhost:5000/api"
        }/orders/${encodeURIComponent(orderId)}/cancel`,
      );

      if (response.data?.success) {
        await fetchOrders();
      } else {
        showModal({
          type: "success",
          title: "Success",
          message: response.data?.message || "Unable to cancel order.",
        });
      }
    } catch (error: any) {
      showModal({
        type: "error",
        title: "error",
        message: error?.data?.message || "Unable to cancel order.",
      });
    } finally {
      setCancelLoadingOrderId(null);
    }
  };

  const handleBuyAgain = (order: any) => {
    if (!order?.items?.length) return;

    const productsToAdd = order.items.map((item: any) => ({
      id: item.id,
      barCode: item.barCode || "",
      productName: item.productName,
      active_price: item.unitPrice,
      image_path: item.image_path || "",
      quantity: Number(item.quantity || 1),
    }));

    addMultipleToCart(productsToAdd);
    navigate("/shopping-cart");
  };

  const handleReview = (order: any) => {
    setExpandedReviewOrderId((prev) =>
      prev === order.orderId ? null : order.orderId,
    );

    setReviewForms((prev) => ({
      ...prev,
      [order.orderId]: prev[order.orderId] || getDefaultReviewForm(),
    }));
  };

  const updateReviewForm = (
    orderId: string,
    field: keyof ReviewFormState,
    value: any,
  ) => {
    setReviewForms((prev) => ({
      ...prev,
      [orderId]: {
        ...(prev[orderId] || getDefaultReviewForm()),
        [field]: value,
      },
    }));
  };

  const invoiceOrders = useMemo(() => {
    return orders.filter(
      (order: any) => String(order.status || "").toLowerCase() === "completed",
    );
  }, [orders]);

  const formatInvoiceDate = (value: string) =>
    new Date(value).toLocaleDateString("en-MY", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

  const formatInvoiceCreatedAt = (value: string) =>
    new Date(value).toLocaleString("en-MY", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });

  const openInvoice = (orderId: string) => {
    window.open(
      `${
        import.meta.env.VITE_LYRAZONE_BASE_URL || "http://localhost:5000/api"
      }/orders/${encodeURIComponent(orderId)}/invoice`,
      "_blank",
    );
  };

  const downloadInvoiceListing = () => {
    if (!invoiceOrders.length) {
      showModal({
        type: "error",
        title: "error",
        message: "No invoice records available to download.",
      });
      return;
    }

    const columns = [
      { header: "Date", dataKey: "date" },
      { header: "Order Number", dataKey: "orderNumber" },
      { header: "Created At", dataKey: "createdAt" },
      { header: "Payment Method", dataKey: "paymentMethod" },
    ];

    const rows = invoiceOrders.map((order: any) => ({
      date: formatInvoiceDate(order.createdAt),
      orderNumber: order.orderId || "-",
      createdAt: formatInvoiceCreatedAt(order.createdAt),
      paymentMethod: order.paymentMethod || "-",
    }));

    exportListingPdf({
      fileName: "invoice-listing.pdf",
      title: "Invoice Listing",
      subtitle: "Subscriber Invoice Records",
      columns,
      rows,
    });
  };

  const handlePhotoChange = (orderId: string, files: FileList | null) => {
    if (!files) return;
    updateReviewForm(orderId, "photos", Array.from(files));
  };

  const handleVideoChange = (orderId: string, files: FileList | null) => {
    if (!files) return;
    updateReviewForm(orderId, "videos", Array.from(files));
  };

  const handleSubmitReview = async (order: Order) => {
    const form = reviewForms[order.orderId] || getDefaultReviewForm();

    if (!form.rating) {
      showModal({
        type: "confirm",
        title: "confirm",
        message: "Please select rating.",
      });
      return;
    }

    try {
      setReviewSubmittingOrderId(order.id);

      const payload = new FormData();
      payload.append("orderId", String(order.id));
      payload.append("productId", String(order.items[0].id));
      payload.append("buyerEmail", buyerEmail);
      payload.append("rating", String(form.rating));
      payload.append("comment", form.comment);

      form.photos.forEach((file) => payload.append("photos", file));
      form.videos.forEach((file) => payload.append("videos", file));

      const response = await axios.post(
        `${
          import.meta.env.VITE_LYRAZONE_BASE_URL || "http://localhost:5000/api"
        }/reviews`,
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data?.success) {
        showModal({
          type: "success",
          title: "success",
          message: "Review submitted successfully.",
        });

        setReviewForms((prev) => ({
          ...prev,
          [order.orderId]: getDefaultReviewForm(),
        }));

        setExpandedReviewOrderId(null);
        await fetchOrders();
      } else {
        showModal({
          type: "success",
          title: "success",
          message: response.data?.message || "Unable to submit review.",
        });
      }
    } catch (error: any) {
      showModal({
        type: "error",
        title: "error",
        message: error?.response?.data?.message || "Unable to submit review.",
      });
    } finally {
      setReviewSubmittingOrderId(null);
    }
  };

  useEffect(() => {
    fetchData();
    fetchTickets();
    fetchOrders();
  }, []);

  useEffect(() => {
    if (validBanners.length <= 1 || isHovered) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % validBanners.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [validBanners, isHovered]);

  useEffect(() => {
    if (validTopSellingProducts.length <= 1 || isTopSellingHovered) return;

    const interval = setInterval(() => {
      setTopSellingIndex((prev) => (prev + 1) % validTopSellingProducts.length);
    }, 2800);

    return () => clearInterval(interval);
  }, [validTopSellingProducts, isTopSellingHovered]);

  useEffect(() => {
    if (validPreviousBuyingProducts.length <= 1 || isPreviousBuyingHovered)
      return;

    const interval = setInterval(() => {
      setPreviousBuyingIndex(
        (prev) => (prev + 1) % validPreviousBuyingProducts.length,
      );
    }, 2800);

    return () => clearInterval(interval);
  }, [validPreviousBuyingProducts, isPreviousBuyingHovered]);

  return (
    <div className="page-content d-flex align-items-stretch">
      {data ? (
        <div className="content-inner chart-cont">
          <div className="row mt-2" id="card-prof">
            <div className="col-md-3">
              <div className="card hovercard">
                <div className="cardheader"></div>
                <Avatar data={data} refreshUser={fetchData} />

                <div className="info">
                  <div className="title">{data.name}</div>
                  <div className="desc">{data.email}</div>
                  <hr />
                </div>

                <nav className="nav text-center prof-nav">
                  <ul className="list-unstyled">
                    <li>
                      <a
                        className={`nav-link ${
                          activeTab === "profile" ? "active" : ""
                        }`}
                        onClick={() => {
                          fetchData();
                          setActiveTab("profile");
                        }}
                      >
                        Profile
                      </a>
                    </li>
                    <li>
                      <a
                        className={`nav-link ${
                          activeTab === "inbox" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("inbox")}
                      >
                        Tickets
                      </a>
                    </li>
                    <li>
                      <a
                        className={`nav-link ${
                          activeTab === "orders" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("orders")}
                      >
                        Orders
                      </a>
                    </li>
                    <li>
                      <a
                        className={`nav-link ${
                          activeTab === "invoices" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("invoices")}
                      >
                        Invoices
                      </a>
                    </li>
                    <li>
                      <a
                        className={`nav-link ${
                          activeTab === "settings" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("settings")}
                      >
                        Setting
                      </a>
                    </li>
                    <li>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          removeToken();
                        }}
                      >
                        Logout
                      </a>
                    </li>
                  </ul>
                </nav>

                <div className="bottom">
                  <a className="btn btn-info btn-sm" href="#">
                    <i className="fa fa-twitter"></i>
                  </a>
                  <a className="btn btn-danger btn-sm" href="#">
                    <i className="fa fa-google-plus"></i>
                  </a>
                  <a className="btn btn-primary btn-sm" href="#">
                    <i className="fa fa-facebook"></i>
                  </a>
                </div>
              </div>
            </div>

            <div className="col-md-9">
              <div className="card hovercard">
                <div className="tab" role="tabpanel">
                  <ul className="nav nav-tabs" role="tablist">
                    <li className="nav-item">
                      <button
                        className={`nav-link ${
                          activeTab === "profile" ? "active" : ""
                        }`}
                        onClick={() => {
                          fetchData();
                          setActiveTab("profile");
                        }}
                      >
                        <i className="fa fa-user"></i> Profile
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${
                          activeTab === "inbox" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("inbox")}
                      >
                        <i className="fa fa-ticket"></i> Tickets
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${
                          activeTab === "orders" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("orders")}
                      >
                        <i className="fa fa-shopping-cart"></i> Orders
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${
                          activeTab === "invoices" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("invoices")}
                      >
                        <i className="fa fa-file-text"></i> Invoices
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${
                          activeTab === "settings" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("settings")}
                      >
                        <i className="fa fa-cog"></i> Settings
                      </button>
                    </li>
                  </ul>

                  <div className="tab-content">
                    {activeTab === "profile" && (
                      <div className="tab-pane fade show active">
                        <p>
                          {data.address === "" ||
                          data.country === "" ||
                          data.city === ""
                            ? "Kindly Complete Your Profile."
                            : "Your Profile is completed"}
                          .{" "}
                        </p>

                        <div className="row mt-3">
                          <h5 className="col-12 mb-2">User Info</h5>

                          <div className="col-6">
                            <div className="form-group">
                              <strong className="mr-5">
                                <i className="fa fa-envelope"></i> E-mail:
                              </strong>{" "}
                              {data.email}
                            </div>
                            <div className="form-group">
                              <strong className="mr-5">
                                <i className="fa fa-phone"></i> Phone:
                              </strong>{" "}
                              {data.phone}
                            </div>
                            <div className="form-group">
                              <strong className="mr-5">
                                <i className="fa fa-map"></i> City:
                              </strong>{" "}
                              {data.city !== "" ? data.city : "NA"}
                            </div>
                          </div>

                          <div className="col-6">
                            <div className="form-group">
                              <strong className="mr-5">
                                <i className="fa fa-globe"></i> Country:
                              </strong>{" "}
                              {data.country !== "" ? data.country : "NA"}
                            </div>
                            <div className="form-group">
                              <strong className="mr-5">
                                <i className="fa fa-map-marker"></i> Address:
                              </strong>{" "}
                              {data.address !== "" ? data.address : "NA"}
                            </div>
                            <div className="form-group">
                              <strong className="mr-5">
                                <i className="fa fa-address-card"></i> Company:
                              </strong>{" "}
                              {data.company_name}
                            </div>
                          </div>
                        </div>
                        <div className="row mt-3">
                          <h5 className="col-12 mb-2">Delivery Details</h5>

                          <div className="col-6">
                            <div className="form-group">
                              <strong className="mr-5">
                                <i className="fa fa-phone"></i> Phone:
                              </strong>{" "}
                              {data.delivery_phone}
                            </div>
                            <div className="form-group">
                              <strong className="mr-5">
                                <i className="fa fa-map"></i> City:
                              </strong>{" "}
                              {data.delivery_city !== ""
                                ? data.delivery_city
                                : "NA"}
                            </div>
                          </div>

                          <div className="col-6">
                            <div className="form-group">
                              <strong className="mr-5">
                                <i className="fa fa-globe"></i> Country:
                              </strong>{" "}
                              {data.delivery_country !== ""
                                ? data.delivery_country
                                : "NA"}
                            </div>
                            <div className="form-group">
                              <strong className="mr-5">
                                <i className="fa fa-map-marker"></i> Address:
                              </strong>{" "}
                              {data.delivery_address !== ""
                                ? data.delivery_address
                                : "NA"}
                            </div>
                            <div className="form-group">
                              <strong className="mr-5">
                                <i className="fa fa-address-card"></i> Company:
                              </strong>{" "}
                              {data.recipient}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {activeTab === "inbox" && (
                      <div className="panel panel-default widget">
                        <div className="panel-heading">
                          <h3 className="panel-title">
                            <i className="fa fa-ticket"></i> Your Tickets
                          </h3>
                        </div>
                        <div className="panel-body" style={{ padding: "5px" }}>
                          {loading ? (
                            <div className="ticket-empty-box">
                              Loading tickets...
                            </div>
                          ) : (
                            <TicketTable
                              tickets={tickets}
                              detailBasePath="/support/tickets"
                            />
                          )}
                        </div>
                      </div>
                    )}

                    {activeTab === "orders" && (
                      <div className="panel panel-default widget">
                        <div className="panel-heading">
                          <h3 className="panel-title">
                            <i className="fa fa-ticket"></i> Your Orders
                          </h3>
                        </div>
                        <section
                          className="orders-page"
                          style={{ padding: "0px" }}
                        >
                          <div className="orders-container">
                            <div className="orders-tabs">
                              {tabs.map((tab) => (
                                <button
                                  key={tab.key}
                                  type="button"
                                  style={{ minWidth: "auto", padding: "0px " }}
                                  className={`orders-tab-btn ${orderActiveTab === tab.key ? "active" : ""}`}
                                  onClick={() => setOrderActiveTab(tab.key)}
                                >
                                  {tab.label} ({getTabCount(tab.key)})
                                </button>
                              ))}
                            </div>

                            <div className="orders-list">
                              {filteredOrders.length === 0 ? (
                                <div className="orders-empty">
                                  No orders found.
                                </div>
                              ) : (
                                filteredOrders.map((order: any) => {
                                  const firstItem = order.items?.[0];
                                  if (!firstItem) return null;

                                  const reviewForm =
                                    reviewForms[order.orderId] ||
                                    getDefaultReviewForm();
                                  const isReviewExpanded =
                                    expandedReviewOrderId === order.orderId;

                                  return (
                                    <div
                                      className="orders-card"
                                      key={order.orderId}
                                    >
                                      <div className="orders-card-top">
                                        <div className="orders-card-left">
                                          <span className="orders-store-name">
                                            LyraZone | {order.orderId}
                                          </span>
                                        </div>

                                        <div
                                          className={`orders-status-badge status-${order.status}`}
                                        >
                                          {getStatusLabel(order.status)}
                                        </div>
                                      </div>

                                      <div
                                        className="orders-product-row"
                                        onClick={() =>
                                          navigate(`/orders/${order.orderId}`)
                                        }
                                      >
                                        <div className="orders-product-image-wrap">
                                          <img
                                            src={getOrderProductImage(
                                              firstItem,
                                            )}
                                            alt={firstItem.productName}
                                            className="orders-product-image"
                                          />
                                        </div>

                                        <div className="orders-product-info">
                                          <h4>{firstItem.productName}</h4>
                                          <p>
                                            Variation:{" "}
                                            {firstItem.variant || "Default"}
                                          </p>
                                          <span>x{firstItem.quantity}</span>
                                          <span>
                                            {new Date(
                                              order.createdAt,
                                            ).toLocaleString()}
                                          </span>
                                        </div>

                                        <div className="orders-product-price-box">
                                          <span className="orders-product-price">
                                            {formatCurrency(
                                              firstItem.unitPrice,
                                            )}
                                          </span>
                                        </div>
                                      </div>

                                      <div className="orders-card-bottom">
                                        <div className="orders-total">
                                          {order.status === "completed"
                                            ? "Order Total:"
                                            : "Amount Payable:"}{" "}
                                          <strong>
                                            {formatCurrency(order.totalAmount)}
                                          </strong>
                                        </div>

                                        <div className="orders-actions">
                                          {(order.status === "to_pay" ||
                                            order.status === "to_ship") && (
                                            <>
                                              <button
                                                type="button"
                                                className="orders-btn orders-btn-muted"
                                              >
                                                {order.status === "to_pay"
                                                  ? "Pending"
                                                  : "Processing"}
                                              </button>

                                              <button
                                                type="button"
                                                className="orders-btn orders-btn-outline"
                                                onClick={() =>
                                                  navigate("/support")
                                                }
                                              >
                                                Contact Us
                                              </button>

                                              {canCancelOrder(order) && (
                                                <button
                                                  type="button"
                                                  className="orders-btn orders-btn-outline"
                                                  onClick={() =>
                                                    handleCancelOrder(
                                                      order.orderId,
                                                    )
                                                  }
                                                  disabled={
                                                    cancelLoadingOrderId ===
                                                    order.orderId
                                                  }
                                                >
                                                  {cancelLoadingOrderId ===
                                                  order.orderId
                                                    ? "Cancelling..."
                                                    : "Cancel Order"}
                                                </button>
                                              )}
                                            </>
                                          )}

                                          {order.status === "completed" && (
                                            <>
                                              <button
                                                type="button"
                                                className="orders-btn orders-btn-primary"
                                                onClick={() =>
                                                  handleBuyAgain(order)
                                                }
                                              >
                                                Buy Again
                                              </button>

                                              {!order.hasReview ? (
                                                <button
                                                  type="button"
                                                  className="orders-btn orders-btn-primary"
                                                  onClick={() =>
                                                    handleReview(order)
                                                  }
                                                >
                                                  {isReviewExpanded
                                                    ? "Hide Review"
                                                    : "Leave a Review"}
                                                </button>
                                              ) : (
                                                <button
                                                  type="button"
                                                  className="orders-btn orders-btn-muted"
                                                  disabled
                                                >
                                                  Reviewed
                                                </button>
                                              )}

                                              <button
                                                type="button"
                                                className="orders-btn orders-btn-outline orders-btn-invoice"
                                                onClick={() =>
                                                  window.open(
                                                    `${
                                                      import.meta.env
                                                        .VITE_LYRAZONE_BASE_URL ||
                                                      "http://localhost:5000/api"
                                                    }/orders/${encodeURIComponent(order.orderId)}/invoice`,
                                                    "_blank",
                                                  )
                                                }
                                              >
                                                Get Invoice
                                              </button>

                                              <button
                                                type="button"
                                                className="orders-btn orders-btn-outline"
                                                onClick={() =>
                                                  navigate("/support")
                                                }
                                              >
                                                Contact Us
                                              </button>
                                            </>
                                          )}

                                          {order.status !== "to_pay" &&
                                            order.status !== "to_ship" &&
                                            order.status !== "completed" && (
                                              <button
                                                type="button"
                                                className="orders-btn orders-btn-outline"
                                                onClick={() =>
                                                  navigate("/support")
                                                }
                                              >
                                                Contact Us
                                              </button>
                                            )}
                                        </div>
                                      </div>

                                      {order.status === "completed" &&
                                        isReviewExpanded && (
                                          <div
                                            className="orders-review-panel"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <div className="orders-review-header">
                                              <button
                                                type="button"
                                                className="orders-review-back"
                                                onClick={() =>
                                                  setExpandedReviewOrderId(null)
                                                }
                                              >
                                                ←
                                              </button>
                                              <h3>Leave a review</h3>
                                            </div>

                                            <div className="orders-review-product">
                                              <div className="orders-review-product-image-wrap">
                                                <img
                                                  src={getOrderProductImage(
                                                    firstItem,
                                                  )}
                                                  alt={firstItem.productName}
                                                  className="orders-review-product-image"
                                                />
                                              </div>

                                              <div className="orders-review-product-info">
                                                <h4>{firstItem.productName}</h4>
                                                <p>Variation: Default</p>
                                              </div>
                                            </div>

                                            <div className="orders-review-upload-row">
                                              <label className="orders-upload-card">
                                                <input
                                                  type="file"
                                                  accept="image/*"
                                                  multiple
                                                  hidden
                                                  onChange={(e) =>
                                                    handlePhotoChange(
                                                      order.orderId,
                                                      e.target.files,
                                                    )
                                                  }
                                                />
                                                <span className="orders-upload-icon">
                                                  🖼️
                                                </span>
                                                <span className="orders-upload-title">
                                                  Photo
                                                </span>
                                                <small>
                                                  {reviewForm.photos.length > 0
                                                    ? `${reviewForm.photos.length} selected`
                                                    : "Add photos"}
                                                </small>
                                              </label>

                                              <label className="orders-upload-card">
                                                <input
                                                  type="file"
                                                  accept="video/*"
                                                  multiple
                                                  hidden
                                                  onChange={(e) =>
                                                    handleVideoChange(
                                                      order.orderId,
                                                      e.target.files,
                                                    )
                                                  }
                                                />
                                                <span className="orders-upload-icon">
                                                  🎥
                                                </span>
                                                <span className="orders-upload-title">
                                                  Video
                                                </span>
                                                <small>
                                                  {reviewForm.videos.length > 0
                                                    ? `${reviewForm.videos.length} selected`
                                                    : "Add videos"}
                                                </small>
                                              </label>
                                            </div>

                                            <div className="orders-review-text-wrap">
                                              <textarea
                                                className="orders-review-textarea"
                                                placeholder="Sharing your thought and experience about this item."
                                                maxLength={3000}
                                                value={reviewForm.comment}
                                                onChange={(e) =>
                                                  updateReviewForm(
                                                    order.orderId,
                                                    "comment",
                                                    e.target.value,
                                                  )
                                                }
                                              />
                                              <div className="orders-review-counter">
                                                {reviewForm.comment.length}/3000
                                              </div>
                                            </div>

                                            <div className="orders-review-rating-row">
                                              <span className="orders-review-rating-label">
                                                * Rating
                                              </span>

                                              <div className="orders-stars">
                                                {[1, 2, 3, 4, 5].map((star) => (
                                                  <button
                                                    key={star}
                                                    type="button"
                                                    className={`orders-star-btn ${
                                                      reviewForm.rating >= star
                                                        ? "active"
                                                        : ""
                                                    }`}
                                                    onClick={() =>
                                                      updateReviewForm(
                                                        order.orderId,
                                                        "rating",
                                                        star,
                                                      )
                                                    }
                                                  >
                                                    ★
                                                  </button>
                                                ))}
                                              </div>

                                              <span className="orders-rating-text">
                                                {reviewForm.rating === 5
                                                  ? "Excellent"
                                                  : reviewForm.rating === 4
                                                    ? "Very Good"
                                                    : reviewForm.rating === 3
                                                      ? "Good"
                                                      : reviewForm.rating === 2
                                                        ? "Fair"
                                                        : "Poor"}
                                              </span>
                                            </div>

                                            <p className="orders-review-guideline">
                                              Please follow the{" "}
                                              <span>review guidelines</span>{" "}
                                              when writing reviews.
                                            </p>

                                            <div className="orders-review-submit-wrap">
                                              <button
                                                type="button"
                                                className="orders-review-submit-btn"
                                                onClick={() =>
                                                  handleSubmitReview(order)
                                                }
                                                disabled={
                                                  reviewSubmittingOrderId ===
                                                  order.id
                                                }
                                              >
                                                {reviewSubmittingOrderId ===
                                                order.id
                                                  ? "Submitting..."
                                                  : "Submit"}
                                              </button>
                                            </div>
                                          </div>
                                        )}
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          </div>
                        </section>
                      </div>
                    )}

                    {activeTab === "invoices" && (
                      <div className="panel panel-default widget">
                        <div className="panel-heading subscriber-invoice-heading">
                          <h3 className="panel-title">
                            <i className="fa fa-file-text"></i> Your Invoices
                          </h3>

                          <button
                            type="button"
                            className="subscriber-invoice-download-btn"
                            onClick={downloadInvoiceListing}
                            title="Download Invoice Listing"
                          >
                            <i className="fa fa-download"></i>
                          </button>
                        </div>

                        <div className="panel-body subscriber-invoice-panel">
                          {orderLoading ? (
                            <div className="ticket-empty-box">
                              Loading invoices...
                            </div>
                          ) : invoiceOrders.length === 0 ? (
                            <div className="ticket-empty-box">
                              No invoices found.
                            </div>
                          ) : (
                            <div className="subscriber-invoice-table-wrap">
                              <table className="subscriber-invoice-table">
                                <thead>
                                  <tr>
                                    <th>Date</th>
                                    <th>Order Number</th>
                                    <th>Created At</th>
                                    <th>Payment Method</th>
                                    <th>View</th>
                                  </tr>
                                </thead>

                                <tbody>
                                  {invoiceOrders.map((order: any) => (
                                    <tr key={order.orderId}>
                                      <td>
                                        {formatInvoiceDate(order.createdAt)}
                                      </td>
                                      <td>{order.orderId}</td>
                                      <td>
                                        {formatInvoiceCreatedAt(
                                          order.createdAt,
                                        )}
                                      </td>
                                      <td>{order.paymentMethod || "-"}</td>
                                      <td>
                                        <button
                                          type="button"
                                          className="subscriber-invoice-view-btn"
                                          onClick={() =>
                                            openInvoice(order.orderId)
                                          }
                                        >
                                          <i className="fa fa-search"></i> View
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {activeTab === "settings" && (
                      <form onSubmit={handleUpdate}>
                        <div className="tab-pane fade show active">
                          <div className="settings-form-card">
                            <div className="settings-form-header">
                              <h3 className="panel-title">
                                <i className="fa fa-comment"></i> Edit Info
                              </h3>

                              <div className="settings-form-actions">
                                {!isEditing ? (
                                  <button
                                    type="button"
                                    className="settings-edit-btn"
                                    onClick={() => setIsEditing(true)}
                                  >
                                    <i className="fa fa-pencil"></i> Edit
                                  </button>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      className="settings-cancel-btn"
                                      onClick={() => setIsEditing(false)}
                                    >
                                      Cancel
                                    </button>

                                    <button
                                      type="submit"
                                      className="settings-save-btn"
                                    >
                                      <i className="fa fa-save"></i> Save
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>

                            <div className="settings-section-title">
                              User Info
                            </div>

                            <div className="row settings-form-grid">
                              <div className="col-md-6">
                                {/* <div className="form-group row"> */}
                                <label className="col-form-label">Name</label>
                                <div className="col-9">
                                  <input
                                    className="form-control"
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                  />
                                </div>
                                {/* </div> */}

                                {/* <div className="form-group row"> */}
                                <label className="col-form-label">Email</label>
                                <div className="col-9">
                                  <input
                                    className="form-control"
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                  />
                                </div>
                                {/* </div> */}

                                {/* <div className="form-group row"> */}
                                <label className="col-form-label">
                                  Company
                                </label>
                                <div className="col-9">
                                  <input
                                    className="form-control"
                                    type="text"
                                    name="company_name"
                                    value={formData.company_name}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                  />
                                </div>
                                {/* </div> */}

                                {/* <div className="form-group row"> */}
                                <label className="col-form-label">
                                  Address
                                </label>
                                <div className="col-9">
                                  <textarea
                                    className="form-control"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    rows={3}
                                  />
                                </div>
                                {/* </div> */}

                                {/* <div className="form-group row"> */}
                                <label className="col-form-label">
                                  Postal Code
                                </label>
                                <div className="col-9">
                                  <input
                                    className="form-control"
                                    type="text"
                                    name="postal_code"
                                    value={formData.postal_code}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                  />
                                </div>
                                {/* </div> */}
                              </div>

                              <div className="col-md-6">
                                {/* <div className="form-group row"> */}
                                <label className="col-form-label">
                                  Country
                                </label>
                                <div className="col-9">
                                  <input
                                    className="form-control"
                                    type="text"
                                    name="country"
                                    value={formData.country}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                  />
                                </div>
                                {/* </div> */}

                                {/* <div className="form-group row"> */}
                                <label className="col-form-label">City</label>
                                <div className="col-9">
                                  <input
                                    className="form-control"
                                    type="text"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                  />
                                </div>
                                {/* </div> */}

                                {/* <div className="form-group row"> */}
                                <label className="col-form-label">Phone</label>
                                <div className="col-9">
                                  <input
                                    className="form-control"
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                  />
                                </div>
                                {/* </div> */}

                                {/* <div className="form-group row"> */}
                                <label className="col-form-label">
                                  Recipient
                                </label>
                                <div className="col-9">
                                  <input
                                    className="form-control"
                                    type="text"
                                    name="recipient"
                                    value={formData.recipient}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                  />
                                </div>
                                {/* </div> */}
                              </div>
                            </div>

                            <div className="settings-divider"></div>
                            <div className="settings-section-title">
                              Delivery Details
                            </div>

                            <div className="row settings-form-grid">
                              <div className="col-md-6">
                                {/* <div className="form-group row"> */}
                                <label className="col-form-label">
                                  Delivery Address
                                </label>
                                <div className="col-9">
                                  <textarea
                                    className="form-control"
                                    name="delivery_address"
                                    value={formData.delivery_address}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    rows={3}
                                  />
                                </div>
                                {/* </div> */}

                                {/* <div className="form-group row"> */}
                                <label className="col-form-label">
                                  Delivery Postal Code
                                </label>
                                <div className="col-9">
                                  <input
                                    className="form-control"
                                    type="text"
                                    name="delivery_postal_code"
                                    value={formData.delivery_postal_code}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                  />
                                </div>
                                {/* </div> */}
                              </div>

                              <div className="col-md-6">
                                {/* <div className="form-group row"> */}
                                <label className="col-form-label">
                                  Delivery Country
                                </label>
                                <div className="col-9">
                                  <input
                                    className="form-control"
                                    type="text"
                                    name="delivery_country"
                                    value={formData.delivery_country}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                  />
                                </div>
                                {/* </div> */}

                                {/* <div className="form-group row"> */}
                                <label className="col-form-label">
                                  Delivery City
                                </label>
                                <div className="col-9">
                                  <input
                                    className="form-control"
                                    type="text"
                                    name="delivery_city"
                                    value={formData.delivery_city}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                  />
                                </div>
                                {/* </div> */}

                                {/* <div className="form-group row"> */}
                                <label className="col-form-label">
                                  Delivery Phone
                                </label>
                                <div className="col-9">
                                  <input
                                    className="form-control"
                                    type="tel"
                                    name="delivery_phone"
                                    value={formData.delivery_phone}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                  />
                                </div>
                                {/* </div> */}
                              </div>
                            </div>
                          </div>
                        </div>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        ""
      )}

      <div className="col-md-3 right-sidebar">
        <div className="right-section">
          <h6 className="right-title">Most Selling Products</h6>

          <div className="top-selling-box">
            {!topSellingLoading && validTopSellingProducts.length > 0 ? (
              <div
                className="top-selling-carousel"
                onMouseEnter={() => setIsTopSellingHovered(true)}
                onMouseLeave={() => setIsTopSellingHovered(false)}
              >
                <div
                  className="top-selling-track"
                  style={{
                    transform: `translateX(-${topSellingIndex * 100}%)`,
                  }}
                >
                  {validTopSellingProducts.map((product) => (
                    <a
                      key={product.id}
                      href={product.link || "#"}
                      className="top-selling-slide"
                      aria-label={product.productName}
                    >
                      <div className="top-selling-card">
                        <img
                          src={getProductImage(
                            product.product_id,
                            product.image_path,
                          )}
                          alt={product.productName}
                          className="top-selling-image"
                        />

                        <div className="top-selling-overlay">
                          <span className="top-selling-badge">
                            🔥 Top Seller
                          </span>

                          <div className="top-selling-name">
                            {product.productName}
                          </div>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>

                {validTopSellingProducts.length > 1 && (
                  <div className="top-selling-dots">
                    {validTopSellingProducts.map((_, index) => (
                      <button
                        key={index}
                        type="button"
                        className={`top-selling-dot ${
                          topSellingIndex === index ? "active" : ""
                        }`}
                        onClick={() => setTopSellingIndex(index)}
                        aria-label={`Go to top selling product ${index + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="top-selling-empty">
                No top selling products available
              </div>
            )}
          </div>
        </div>

        <div className="right-section mt-4">
          <h6 className="right-title">Previous Buying</h6>

          <div className="previous-buying-box">
            {!previousBuyingLoading &&
            validPreviousBuyingProducts.length > 0 ? (
              <div
                className="previous-buying-carousel"
                onMouseEnter={() => setIsPreviousBuyingHovered(true)}
                onMouseLeave={() => setIsPreviousBuyingHovered(false)}
              >
                <div
                  className="previous-buying-track"
                  style={{
                    transform: `translateX(-${previousBuyingIndex * 100}%)`,
                  }}
                >
                  {validPreviousBuyingProducts.map((product) => (
                    <a
                      key={product.product_id}
                      href={product.link || "#"}
                      className="previous-buying-slide"
                      aria-label={product.productName}
                    >
                      <div className="previous-buying-card">
                        <img
                          src={getPreviousBuyingImage(
                            product.product_id,
                            product.image_path,
                          )}
                          alt={product.productName}
                          className="previous-buying-image"
                        />

                        <div className="previous-buying-overlay">
                          <span className="previous-buying-badge">
                            🛒 Bought Before
                          </span>

                          <div className="previous-buying-name">
                            {product.productName}
                          </div>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>

                {validPreviousBuyingProducts.length > 1 && (
                  <div className="previous-buying-dots">
                    {validPreviousBuyingProducts.map((_, index) => (
                      <button
                        key={index}
                        type="button"
                        className={`previous-buying-dot ${
                          previousBuyingIndex === index ? "active" : ""
                        }`}
                        onClick={() => setPreviousBuyingIndex(index)}
                        aria-label={`Go to previous buying product ${index + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="previous-buying-empty">
                No previous buying products available
              </div>
            )}
          </div>
        </div>

        <div className="right-section mt-4">
          <h6 className="right-title">Promotions</h6>

          <div className="promotion-box">
            {!loading && validBanners.length > 0 ? (
              <div
                className="promotion-carousel"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <div
                  className="promotion-track"
                  style={{ transform: `translateX(-${activeIndex * 100}%)` }}
                >
                  {validBanners.map((banner) => (
                    <a
                      key={banner.id}
                      href={banner.link || "#"}
                      className="promotion-slide"
                      target={
                        banner.link?.startsWith("http") ? "_blank" : "_self"
                      }
                      rel="noreferrer"
                      aria-label={banner.title}
                    >
                      <div className="promotion-card">
                        <img
                          src={getBannerImage(banner.image)}
                          alt={banner.title}
                          className="promotion-image"
                        />

                        <span className="promotion-cta">
                          <span className="promotion-cta-text">
                            ✨ Get Promotion
                          </span>
                        </span>
                      </div>
                    </a>
                  ))}
                </div>

                {validBanners.length > 1 && (
                  <div className="promotion-dots">
                    {validBanners.map((_, index) => (
                      <button
                        key={index}
                        type="button"
                        className={`promotion-dot ${
                          activeIndex === index ? "active" : ""
                        }`}
                        onClick={() => setActiveIndex(index)}
                        aria-label={`Go to banner ${index + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="promotion-empty">No promotions available</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriberProfile;
